/**
 * ListingCard component
 *
 * Displays a single listing in a card format with key information
 * and interactive features.
 */

import React from "react";
import { MapPin, Train, Maximize, Building, Calendar } from "lucide-react";
import { useTranslation } from "@/shared/hooks";
import type { Listing } from "@/shared/types/listings";

interface ListingCardProps {
  listing: Listing;
  onClick?: (listing: Listing) => void;
  isSelected?: boolean;
  onHover?: (listing: Listing | null) => void;
}

/**
 * Format price as JPY
 */
function formatPrice(price: number): string {
  return new Intl.NumberFormat("ja-JP", {
    style: "currency",
    currency: "JPY",
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Get placeholder image URL
 */
function getPlaceholderImage(_index: number = 0): string {
  return "https://via.placeholder.com/300x200/e5e7eb/9ca3af?text=No+Image";
}

export const ListingCard: React.FC<ListingCardProps> = ({
  listing,
  onClick,
  isSelected = false,
  onHover,
}) => {
  const t = useTranslation();

  const handleClick = () => {
    onClick?.(listing);
  };

  const handleMouseEnter = () => {
    onHover?.(listing);
  };

  const handleMouseLeave = () => {
    onHover?.(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.(listing);
    }
  };

  // Get thumbnail or first image or placeholder from composed media structure
  const getFirstImage = (): string => {
    const sections = listing.media.images_by_section || {};

    // Try to find featured image first
    for (const images of Object.values(sections)) {
      const featuredImage = images.find((img) => img.is_featured);
      if (featuredImage) {
        return featuredImage.thumbnail_url || featuredImage.url;
      }
    }

    // Fall back to first available image
    const allImages = Object.values(sections).flat();
    return (
      allImages[0]?.thumbnail_url || allImages[0]?.url || getPlaceholderImage()
    );
  };

  const imageUrl = getFirstImage();

  // Format location string from composed location block
  const locationParts = [
    listing.location.ward?.ja,
    listing.location.city.ja,
    listing.location.prefecture.ja,
  ].filter(Boolean);
  const locationString = locationParts.join(", ");

  // Format station info from composed location.stations
  const firstStation = listing.location.stations[0];
  const stationInfo = firstStation
    ? `${firstStation.name.ja} (${firstStation.walk_minutes} ${t("listings.min")})`
    : null;

  // Get key amenity badges from composed amenities structure
  const keyAmenities = [];
  if (listing.amenities.contract.foreigner_friendly) {
    keyAmenities.push({
      key: "foreigner",
      label: t("listings.foreignerFriendly"),
    });
  }
  if (listing.amenities.contract.pets_allowed) {
    keyAmenities.push({ key: "pets", label: t("listings.petsAllowed") });
  }
  if (listing.amenities.comfort.air_conditioning) {
    keyAmenities.push({ key: "ac", label: t("listings.airConditioning") });
  }

  // Calculate building age from composed property block
  const buildingAge = listing.property.year_built
    ? new Date().getFullYear() - listing.property.year_built
    : null;

  return (
    <article
      className={`
        bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer
        transition-all duration-200 hover:shadow-md hover:border-gray-300
        ${isSelected ? "ring-2 ring-primary-500 border-primary-500" : ""}
      `}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`${listing.title.ja} - ${formatPrice(listing.financials.rent.amount)}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
        <img
          src={imageUrl}
          alt={listing.title.ja}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.src = getPlaceholderImage();
          }}
        />
        {/* Room layout badge */}
        <div className="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs font-medium">
          {listing.property.room_layout}
        </div>
      </div>

      {/* Content - all inside the card border */}
      <div className="p-3 border-t border-gray-100">
        {/* Price */}
        <div className="mb-2">
          <span className="text-lg font-bold text-gray-900">
            {formatPrice(listing.financials.rent.amount)}
          </span>
          <span className="text-xs text-gray-500 ml-1">
            /{t("listings.month")}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-medium text-gray-800 line-clamp-1 text-sm mb-2">
          {listing.title.ja}
        </h3>

        {/* Location with icon */}
        <div className="flex items-center gap-1.5 text-gray-600 text-xs mb-1">
          <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
          <span className="line-clamp-1">{locationString}</span>
        </div>

        {/* Station with icon */}
        {stationInfo && (
          <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-2">
            <Train className="w-3 h-3 text-gray-400 flex-shrink-0" />
            <span>{stationInfo}</span>
          </div>
        )}

        {/* Stats row with icons */}
        <div className="flex items-center gap-3 text-xs text-gray-600 mb-2 pt-2 border-t border-gray-100">
          <span className="flex items-center gap-1">
            <Maximize className="w-3 h-3 text-gray-400" />
            {listing.property.area_sqm} m²
          </span>
          {listing.property.floor && (
            <span className="flex items-center gap-1">
              <Building className="w-3 h-3 text-gray-400" />
              {listing.property.floor}F
            </span>
          )}
          {buildingAge !== null && (
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3 text-gray-400" />
              {buildingAge}y
            </span>
          )}
        </div>

        {/* Deposit/Key money */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
          <span>
            {t("listings.deposit")}:{" "}
            {!listing.financials.deposit
              ? "0"
              : `${Math.round(listing.financials.deposit.amount / listing.financials.rent.amount)}M`}
          </span>
          <span className="text-gray-300">•</span>
          <span>
            {t("listings.keyMoney")}:{" "}
            {!listing.financials.key_money
              ? "0"
              : `${Math.round(listing.financials.key_money.amount / listing.financials.rent.amount)}M`}
          </span>
        </div>

        {/* Amenity badges */}
        {keyAmenities.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-100">
            {keyAmenities.slice(0, 3).map((amenity) => (
              <span
                key={amenity.key}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600"
              >
                {amenity.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

export default ListingCard;
